import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { onPhoneGate } from "@/lib/phoneGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpInput } from "@/components/OtpInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * The inline "verify your mobile" sheet, opened at the FIRST action that needs reachability (see
 * lib/phoneGate.js) instead of gating all browsing. Reuses the existing attach-phone API
 * (`/auth/phone/request` + `/auth/phone/verify`, the same ones `VerifyPhone` drives). On success
 * the session is refreshed and the waiting action is resumed; closing the sheet abandons it.
 */
export function PhoneGate() {
  const { t, setSession, refreshCart } = useApp();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [devCode, setDevCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const resolver = useRef(null);

  useEffect(() => {
    onPhoneGate((resolve) => {
      resolver.current = resolve;
      setStep("phone");
      setCode("");
      setOtpError("");
      setOpen(true);
    });
    return () => onPhoneGate(null);
  }, []);

  const finish = (verified) => {
    const resolve = resolver.current;
    resolver.current = null;
    setOpen(false);
    if (resolve) resolve(verified);
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/request", { phone });
      setRequestId(data.request_id);
      setNormalizedPhone(data.phone);
      setDevCode(data.dev_code || "");
      setOtpError("");
      setStep("otp");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    setOtpError("");
    try {
      const { data } = await api.post("/auth/phone/verify", { request_id: requestId, code });
      setSession(data);
      refreshCart().catch(() => {});
      toast.success(t("auth.phoneLinked"));
      finish(true);
    } catch (e) {
      const status = e?.response?.status;
      const rejected = status >= 400 && status < 500 && status !== 429;
      setOtpError(rejected ? t("auth.otpWrong") : apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish(false)}>
      <DialogContent data-testid="phone-gate">
        <DialogHeader>
          <DialogTitle>{t("auth.phoneGateTitle")}</DialogTitle>
          <DialogDescription>{t("auth.phoneGateSubtitle")}</DialogDescription>
        </DialogHeader>
        {step === "phone" ? (
          <div className="space-y-4" data-testid="phone-gate-phone-step">
            <div className="space-y-2">
              <Label htmlFor="gate-phone">{t("auth.phoneLabel")}</Label>
              <Input
                id="gate-phone"
                data-testid="phone-gate-phone-input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={phone}
                placeholder={t("auth.phonePlaceholder")}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && phone && sendOtp()}
              />
            </div>
            <Button
              className="w-full rounded-full"
              data-testid="phone-gate-send-otp-btn"
              disabled={loading || !phone}
              onClick={sendOtp}
            >
              {loading ? t("common.loading") : t("auth.sendOtp")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="phone-gate-otp-step">
            <p className="text-sm text-muted-foreground">{t("auth.otpSubtitle", { phone: normalizedPhone })}</p>
            {devCode && (
              <div
                data-testid="phone-gate-dev-otp-notice"
                className="flex items-center gap-2 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-4 py-3 text-sm"
              >
                <ShieldCheck size={16} className="text-[hsl(var(--accent))]" />
                {t("auth.devNotice", { code: devCode })}
              </div>
            )}
            <OtpInput
              id="gate-otp"
              testId="phone-gate-otp-input"
              errorTestId="phone-gate-otp-error"
              label={t("auth.otpLabel")}
              value={code}
              onChange={setCode}
              onEdit={() => setOtpError("")}
              onSubmit={() => code.length === 6 && verify()}
              error={otpError}
            />
            <Button
              className="w-full rounded-full"
              data-testid="phone-gate-verify-btn"
              disabled={loading || code.length < 6}
              onClick={verify}
            >
              {loading ? t("common.loading") : t("auth.verify")}
            </Button>
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground"
              data-testid="phone-gate-change-btn"
              onClick={() => setStep("phone")}
            >
              <ArrowLeft size={14} /> {t("auth.changeNumber")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
